import { useState } from 'react'
import { toast } from 'sonner'
import { useManufacturer } from '@/api/hooks'
import {
  useCreateSourcingOption,
  useSetSourceCategories,
  useSourcingCategories,
  useSuppliers,
  type Supplier,
} from '@/api/sourcing'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { SourcingCategoryPicker } from '@/components/sourcing-category-picker'
import { SupplierPicker } from '@/components/supplier-picker'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { formatApiErrorMessage } from '@/lib/api-error'
import { useChangeSourceAction } from './hooks'

const nameKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '')

type UseFor = 'categories' | 'this_po'

/**
 * The supplier said the order must be bought through someone else (USA-1506): pick that source,
 * starting from the company they named, and the PO is bought and placed there. A supplier that
 * is not yet one of the manufacturer's sources is added as one, and a backup source can also take
 * future POs of only the chosen categories (starting from this PO's), such as parts ordered
 * without equipment.
 */
export function SourceChangeForm({
  poUuid,
  manufacturerUuid,
  manufacturerName,
  actionId,
  requiredSource,
  onDone,
}: {
  poUuid: string
  manufacturerUuid: string
  manufacturerName: string
  actionId: number
  requiredSource: string | null
  onDone: () => void
}) {
  const manufacturer = useManufacturer(manufacturerUuid)
  const suppliers = useSuppliers()
  const categories = useSourcingCategories(manufacturerUuid, poUuid)
  const createSource = useCreateSourcingOption(manufacturerUuid)
  const setCategories = useSetSourceCategories(manufacturerUuid)
  const changeSource = useChangeSourceAction(poUuid)
  const [picked, setPicked] = useState<{ supplier: Supplier | null } | null>(null)
  const [useFor, setUseFor] = useState<UseFor | null>(null)
  const [pickedCategories, setPickedCategories] = useState<number[] | null>(null)

  const named = requiredSource
    ? (suppliers.data ?? []).find(
        (supplier) =>
          supplier.kind !== 'buying_group' && nameKey(supplier.name) === nameKey(requiredSource),
      )
    : undefined
  const chosen = picked ? picked.supplier : named
  const sources = manufacturer.data?.sourcing_options ?? []
  const existing =
    chosen === undefined
      ? undefined
      : sources.find(
          (option) => (option.supplier?.id ?? null) === (chosen?.id ?? null) && !option.buying_group,
        )
  const chosenName = chosen === undefined ? null : (chosen?.name ?? manufacturerName)
  // The preferred source takes every PO no backup source takes, so it has no categories.
  const becomesBackup =
    chosen !== undefined && (existing ? existing.rank > 1 : !(sources.length === 0 && chosen === null))
  const existingCategories = existing
    ? (categories.data?.sources.find((source) => source.sourcing_option_id === existing.id)
        ?.taxonomy_node_ids ?? [])
    : []
  const poCategories = categories.data?.purchase_order_category_ids ?? []
  const categoryValue =
    pickedCategories ?? [...new Set([...existingCategories, ...poCategories])]
  const useForValue: UseFor =
    useFor ?? (existingCategories.length || poCategories.length ? 'categories' : 'this_po')
  const pending = createSource.isPending || setCategories.isPending || changeSource.isPending

  const submit = async () => {
    if (chosen === undefined) return
    try {
      // The first source becomes the manufacturer's preferred one, so a manufacturer bought
      // direct with no sources on file records direct first and gets this one as a backup.
      if (!existing && sources.length === 0 && chosen !== null) {
        await createSource.mutateAsync({ supplier_id: null })
      }
      const sourcingOptionId =
        existing?.id ?? (await createSource.mutateAsync({ supplier_id: chosen?.id ?? null })).id
      if (becomesBackup && useForValue === 'categories' && categoryValue.length) {
        await setCategories.mutateAsync({ optionId: sourcingOptionId, taxonomyNodeIds: categoryValue })
      }
      await changeSource.mutateAsync({ actionId, sourcingOptionId })
      toast.success(`PO now ordered through ${chosenName}`)
      onDone()
    } catch (error) {
      toast.error(formatApiErrorMessage('Source not changed', error))
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`source-change-${actionId}`}>Order through</Label>
        <SupplierPicker
          id={`source-change-${actionId}`}
          value={chosen === undefined ? undefined : (chosen?.id ?? null)}
          initialSearch={named ? '' : (requiredSource ?? '')}
          onChange={(supplier) => {
            setPicked({ supplier })
            setPickedCategories(null)
            setUseFor(null)
          }}
          disabled={pending || manufacturer.isLoading}
          className="sm:max-w-sm"
        />
        {chosen !== undefined && !existing && !manufacturer.isLoading && (
          <p className="text-xs text-muted-foreground">
            Adds {chosenName} as one of {manufacturerName}&rsquo;s sources.
          </p>
        )}
      </div>
      {becomesBackup && chosenName && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Use {chosenName} for</p>
          <ReviewChoiceGroup<UseFor>
            label={`Use ${chosenName} for`}
            value={useForValue}
            options={[
              { value: 'categories', label: 'Orders of only these categories' },
              { value: 'this_po', label: 'This PO only' },
            ]}
            onChange={setUseFor}
            disabled={pending}
          />
          {useForValue === 'categories' && (
            <div className="space-y-1.5">
              <SourcingCategoryPicker
                categories={categories.data?.categories ?? []}
                value={categoryValue}
                onChange={setPickedCategories}
                disabled={pending || categories.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                New {manufacturerName} POs go to {chosenName} when everything on them is in these
                categories. Orders with other products stay with the preferred source.
              </p>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          size="sm"
          loading={pending}
          loadingText="Switching…"
          disabled={
            chosen === undefined ||
            manufacturer.isLoading ||
            (becomesBackup && useForValue === 'categories' && categoryValue.length === 0)
          }
          onClick={submit}
        >
          {chosenName ? `Order through ${chosenName}` : 'Choose a source'}
        </LoadingButton>
        <Button size="sm" variant="ghost" onClick={onDone} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
